import classes from "./SelectToolActionButton.module.css";
import { CARDS_DATA } from "../../../constants";
import { useNavigate } from "react-router-dom";
import buttonClasses from "../../ToolSelection/ToolSelection.module.css"; // retained for structural width only after refactor
import util from "../../../styles/utilities.module.css";
import InfoWrapper from "../../UI/InfoWrapper/InfoWrapper";
import { useSelector } from "react-redux";
import { useState } from "react";
import { selectFormType } from "../../../slices/form";
import Ribbon from "./Ribbon";
import { Tooltip } from "@mui/material";

const toolTestIdMapper = {
  0: 'assessment-tool-button',
  1: 'reading-comprehension-tool-button',
  2: 'grading-tool-button',
  3: 'sipps-tool-button',
  4: 'text-leveling-tool-button',
  5: 'training-tool-button',
};

const SelectToolActionButton = ({
  text,
  isSelected,
  handleOnClick = () => { },
  buttonType,
  cardIndex,
  showRibbon,
  toolColor,
  buttonColor,
  navigateToNextPageHandler = () => { },
  buttonLabel = "Select",
  trainingData,
}) => {
  const { subtitle = "" } = CARDS_DATA[cardIndex] || {};
  const navigate = useNavigate();

  const navigateToNextPage = () => {
    localStorage.setItem("from", "toolSelection");
    if (cardIndex === 2) {
      navigateToNextPageHandler();
    } else if (cardIndex === 4) {
      navigate("/textLeveling");
    } else if (cardIndex === 5) {
      navigate("/credentialing");
    } else {
      navigate("/criteriaselection");
    }
  };

  const [showModelWraper, setShowModelWraper] = useState(false);
  const formType = useSelector(selectFormType);

  const descriptions = {
    assessment: `This tool allows you to generate a set of questions 
                for content you upload. Your content can be a fictional 
                or non-fiction text, a textbook chapter, a poem, 
                a lesson plan, a syllabus, a list of terms, a list of 
                vocab words, etc. You will be able to export the questions, 
                along with the answer key, to a Google Doc, Google Form, Kahoot, or Blooket sheet.`,
    reading: `This tool allows you to create a brand new, original text based on a topic or 
              a description of what you'd like the AI to write about. We 
              support a wide range of genres, covering both fiction and 
              non-fiction. Once the text has been created, you can generate 
              a set of questions for the text. You will be able to export the 
              text and questions, along with the answer key, 
              to a Google Doc, Google Form, Kahoot, or Blooket sheet.`,
    grading: `This tool relieves you of the tedious task of grading student papers while 
              keeping you firmly in control. Simply upload your assignment, rubric (if 
              you have one), a few graded samples, and the student work you want to 
              grade. Our AI will suggest grades and detailed feedback aligned with your 
              criteria. And the best part? You're always in control—you can adjust grades 
              or personalize feedback however you like. Once you're happy, you can export 
              everything to Google Docs.`,
    sipps: `This tool allows you to generate matching, cut and paste, and reading practice
            worksheets that target the selected level and lesson number of your school's ELA curriculum.`,
    textLeveling: (
      <>
        This tool helps you adjust the complexity of your text to make it more accessible
        for your target audience. Simply paste your text, select your desired grade level
        or{" "}
        <Tooltip title="Lexile® is a registered trademark of MetaMetrics, Inc. This tool is not affiliated with MetaMetrics and does not generate official Lexile® measures.">
          <span>
            <span style={{ textDecoration: "underline", cursor: "help" }}>
              Lexile
            </span>
            ®
          </span>
        </Tooltip>
        , and the tool will generate a version of your text that matches
        the selected complexity level using AI-based estimates. This is perfect for adapting materials to various
        reading abilities.
      </>
    ),
    training: `Complete structured training modules to master ClassGenius tools and earn your 
               ClassGenius Fluency Badge. These modules cover effective AI integration in education
               and provide hands-on practice with our platform's features.`,
  };

  return (
    <div
      data-testid={toolTestIdMapper[cardIndex]}
      name={text}
      style={{ borderColor: toolColor }}
      className={`${classes["select-tool-action-button"]} ${isSelected ? classes["select-tool-action-button--selected"] : ""
        }`}
      onClick={handleOnClick}
      type={buttonType}
    >
      <div className={classes["text-container"]}>{text}</div>
      {subtitle ? (
        <div className={classes["subtitle-container"]}
          style={{ backgroundColor: toolColor }}
        >
          <p>{subtitle}</p>
        </div>
      ) : null}
      {cardIndex === 5 && trainingData && trainingData.badges && trainingData.badges.length > 0 && (
        <div className={classes["training-progress"]} style={{ padding: "0.75rem", background: "#f3f4f6", margin: "0.5rem 0", borderRadius: "0.5rem" }}>
          {/* Show individual badge progress with targets */}
          {trainingData.badges.map((badge, index) => {
            const isEarned = badge.progress.is_earned;
            const badgeName = badge.badge.name;
            const badgeType = badge.badge.badge_type;
            
            // Get overall badge completion progress (combined modules + earning progress)
            let progressText = '';
            if (isEarned) {
              progressText = 'Badge earned!';
            } else {
              // Show only percentage, not status text
              const overallProgress = badge.progress.overall_progress_percentage || 0;
              progressText = `${Math.round(overallProgress)}%`;
            }
            
            return (
              <div key={index} style={{ marginBottom: index < trainingData.badges.length - 1 ? "0.5rem" : "0" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                  <div style={{ fontSize: "0.85rem", minWidth: "16px", lineHeight: "1.2" }}>
                    {isEarned ? "✅" : "⭕"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "0.85rem", color: isEarned ? "#10b981" : "#6b7280", fontWeight: "500", lineHeight: "1.2", marginBottom: "0.25rem" }}>
                      {badgeName}
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#6b7280", lineHeight: "1.2" }}>
                      {progressText}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div
        className={classes["button-container"]}
      >
        <button
          className={`${util.btn} ${util['btn--accentGradient']} ${util.elevateHover}  ${buttonClasses.selectButton}`}
          onClick={navigateToNextPage}
        >
          {buttonLabel}
        </button>
      </div>
      <div className={classes["info-button-container"]}>
        <button
          onClick={() => setShowModelWraper(true)}
          className={`${util.btn} ${util['btn--ghost']} ${buttonClasses.infoButton}`}
          type="button"
        >
          more info
        </button>
      </div>
      <InfoWrapper
        modalProps={{
          isOpen: showModelWraper,
          setIsOpen: setShowModelWraper,
          description: descriptions[formType] || "",
        }}
      />
      {showRibbon && <Ribbon />}
    </div>
  );
};

export default SelectToolActionButton;
